import { useState, type FormEvent } from 'react';
import type { ItemResponse, ListResponse, ProjectCreate, ProjectRecord, ProjectUpdate } from '@jarvis/shared';
import { PROJECT_STATUS_LABEL, PROJECT_STATUSES, type ProjectStatus } from '@jarvis/shared/constants';
import { Icon } from '../components/Icon';
import { toast } from '../components/toast';
import { EmptyState, ErrorState, Progress, Skeleton } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { fmt } from '../lib/format';
import { useLive } from '../lib/useLive';

const STATUS: readonly ProjectStatus[] = PROJECT_STATUSES;
const TONE: Record<ProjectStatus, string> = { active: 'badge-signal', planning: 'badge-info', paused: 'badge-warn', done: 'badge-off' };

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Algo deu errado.');

interface FormValues { name: string; description: string; status: ProjectStatus; dueDate: string; nextStep: string }
const EMPTY: FormValues = { name: '', description: '', status: 'active', dueDate: '', nextStep: '' };

function toValues(p: ProjectRecord): FormValues {
  return { name: p.name, description: p.description, status: p.status, dueDate: p.dueDate ?? '', nextStep: p.nextStep };
}

function ProjectForm({ initial, submitLabel, onSubmit, onCancel }: {
  initial: FormValues; submitLabel: string; onSubmit: (v: FormValues) => Promise<void>; onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const field = (k: keyof FormValues) => ({
    value: v[k],
    onChange: (e: { target: { value: string } }) => setV((s) => ({ ...s, [k]: e.target.value })),
    'aria-invalid': errors[k] ? true : undefined
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit(v);
    } catch (err) {
      if (err instanceof ApiError && err.fields) setErrors(err.fields);
      else toast(errMsg(err), 'alert');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form-grid" onSubmit={submit} noValidate>
      <div className="field span-2"><label htmlFor="pf-name">Nome</label>
        <input className="input" id="pf-name" maxLength={120} required autoFocus {...field('name')} />
        {errors.name && <span className="field-error">{errors.name}</span>}</div>
      <div className="field span-2"><label htmlFor="pf-desc">Descrição</label>
        <textarea className="input" id="pf-desc" rows={2} maxLength={2000} {...field('description')} />
        {errors.description && <span className="field-error">{errors.description}</span>}</div>
      <div className="field"><label htmlFor="pf-status">Situação</label>
        <select className="input" id="pf-status" {...field('status')}>
          {STATUS.map((s) => <option key={s} value={s}>{PROJECT_STATUS_LABEL[s]}</option>)}
        </select></div>
      <div className="field"><label htmlFor="pf-due">Prazo</label>
        <input className="input" id="pf-due" type="date" {...field('dueDate')} />
        {errors.dueDate && <span className="field-error">{errors.dueDate}</span>}</div>
      <div className="field span-2"><label htmlFor="pf-next">Próximo passo</label>
        <input className="input" id="pf-next" maxLength={300} placeholder="O que precisa acontecer agora?" {...field('nextStep')} />
        {errors.nextStep && <span className="field-error">{errors.nextStep}</span>}</div>
      <div className="chips span-2">
        <button type="submit" className="btn btn-primary" disabled={busy}><Icon name="check" />{busy ? 'Salvando…' : submitLabel}</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancelar</button>
      </div>
    </form>
  );
}

function payload(v: FormValues): ProjectCreate {
  return { name: v.name, description: v.description, status: v.status, dueDate: v.dueDate || null, nextStep: v.nextStep };
}

function ProjectCard({ p, onChange, onRemove }: { p: ProjectRecord; onChange: (p: ProjectRecord) => void; onRemove: (id: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newTask, setNewTask] = useState('');
  const done = p.tasks.filter((t) => t.done).length;
  const progress = p.tasks.length ? (done / p.tasks.length) * 100 : 0;

  const run = async (fn: () => Promise<ProjectRecord | void>, success?: string) => {
    setBusy(true);
    try {
      const updated = await fn();
      if (updated) onChange(updated);
      if (success) toast(success, 'check');
    } catch (err) {
      toast(errMsg(err), 'alert');
    } finally {
      setBusy(false);
    }
  };

  const patch = (body: ProjectUpdate, success?: string) =>
    run(async () => (await api.patch<ItemResponse<ProjectRecord>>(`/api/projects/${p.id}`, body)).item, success);

  const addTask = (e: FormEvent) => {
    e.preventDefault();
    const title = newTask.trim();
    if (!title) return;
    run(async () => {
      const r = await api.post<ItemResponse<ProjectRecord>>(`/api/projects/${p.id}/tasks`, { title });
      setNewTask('');
      return r.item;
    });
  };

  if (editing) {
    return (
      <article className="card project">
        <h3>Editar projeto</h3>
        <ProjectForm initial={toValues(p)} submitLabel="Salvar"
          onCancel={() => setEditing(false)}
          onSubmit={async (v) => {
            const r = await api.patch<ItemResponse<ProjectRecord>>(`/api/projects/${p.id}`, payload(v));
            onChange(r.item);
            setEditing(false);
            toast('Projeto atualizado.', 'check');
          }} />
      </article>
    );
  }

  return (
    <article className="card project" aria-busy={busy}>
      <div className="chips">
        <label className="sr-only" htmlFor={`st-${p.id}`}>Situação de {p.name}</label>
        <select id={`st-${p.id}`} className={`badge badge-select ${TONE[p.status]}`} value={p.status} disabled={busy}
          onChange={(e) => patch({ status: e.target.value as ProjectStatus })}>
          {STATUS.map((s) => <option key={s} value={s}>{PROJECT_STATUS_LABEL[s]}</option>)}
        </select>
        {p.archivedAt && <span className="badge badge-off">Arquivado</span>}
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-ghost btn-icon" title="Editar" aria-label={`Editar ${p.name}`} onClick={() => setEditing(true)}><Icon name="edit" /></button>
      </div>
      <h3>{p.name}</h3>
      {p.description && <p className="desc">{p.description}</p>}
      <div className="usage-row">
        <div className="top"><span className="row-sub">{done} de {p.tasks.length} tarefas</span><span className="mono">{Math.round(progress)}%</span></div>
        <Progress value={progress} />
      </div>
      {p.nextStep && <div className="next"><b>Próximo passo</b>{p.nextStep}</div>}

      <ul className="tasks">
        {p.tasks.map((t) => (
          <li key={t.id} className="task-row">
            <label>
              <input type="checkbox" checked={t.done} disabled={busy}
                onChange={() => run(async () => (await api.patch<ItemResponse<ProjectRecord>>(`/api/tasks/${t.id}`, { done: !t.done })).item)} />
              <span>{t.title}</span>
            </label>
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Excluir tarefa ${t.title}`} title="Excluir tarefa" disabled={busy}
              onClick={() => run(async () => (await api.del<ItemResponse<ProjectRecord>>(`/api/tasks/${t.id}`)).item)}><Icon name="trash" /></button>
          </li>
        ))}
      </ul>
      <form className="inline-form" onSubmit={addTask}>
        <label className="sr-only" htmlFor={`nt-${p.id}`}>Nova tarefa em {p.name}</label>
        <input id={`nt-${p.id}`} className="input" placeholder="Adicionar tarefa…" maxLength={200} value={newTask} onChange={(e) => setNewTask(e.target.value)} disabled={busy} />
        <button type="submit" className="btn btn-sm" disabled={busy || !newTask.trim()}>Adicionar</button>
      </form>

      <div className="card-foot">
        <span className="row-sub">{p.dueDate ? <>Prazo {fmt.dateOnly(p.dueDate)}</> : 'Sem prazo'}</span>
        <span className="spacer" />
        {confirming ? (
          <>
            <span className="row-sub">Excluir{p.tasks.length ? ` com ${p.tasks.length} tarefa(s)` : ''}?</span>
            <button type="button" className="btn btn-sm btn-danger" disabled={busy}
              onClick={() => run(async () => { await api.del(`/api/projects/${p.id}`); onRemove(p.id); }, 'Projeto excluído.')}>Excluir</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirming(false)}>Cancelar</button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy}
              onClick={() => patch({ archived: !p.archivedAt }, p.archivedAt ? 'Projeto restaurado.' : 'Projeto arquivado.')}>
              {p.archivedAt ? 'Restaurar' : 'Arquivar'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConfirming(true)}><Icon name="trash" />Excluir</button>
          </>
        )}
      </div>
    </article>
  );
}

export function ProjectsLive() {
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const { state, set, reload } = useLive(`/api/projects${archived ? '?archived=1' : ''}`, (b: ListResponse<ProjectRecord>) => b.items);

  const replace = (p: ProjectRecord) => set((list) => {
    const stillHere = archived ? Boolean(p.archivedAt) : !p.archivedAt;
    return stillHere ? list.map((x) => (x.id === p.id ? p : x)) : list.filter((x) => x.id !== p.id);
  });
  const remove = (id: string) => set((list) => list.filter((x) => x.id !== id));

  let body;
  if (state.status === 'loading') body = <Skeleton kind="cards" count={3} />;
  else if (state.status === 'error') body = <ErrorState error={state.error} onRetry={reload} />;
  else if (!state.data.length && !creating) {
    body = archived
      ? <EmptyState icon="folder" title="Nenhum projeto arquivado" text="Projetos arquivados ficam guardados aqui e podem ser restaurados." />
      : <EmptyState icon="folder" title="Nenhum projeto ainda" text="Crie seu primeiro projeto. Ele fica salvo no banco de dados do Jarvis e pode ser exportado ou apagado quando você quiser."
          actions={[{ label: 'Criar projeto', icon: 'edit', primary: true, onClick: () => setCreating(true) }]} />;
  } else {
    body = <div className="projects">{state.data.map((p) => <ProjectCard key={p.id} p={p} onChange={replace} onRemove={remove} />)}</div>;
  }

  return (
    <div className="stack">
      <div className="toolbar">
        <div className="chips" role="group" aria-label="Mostrar">
          <button type="button" className="chip" aria-pressed={!archived} onClick={() => setArchived(false)}>Ativos</button>
          <button type="button" className="chip" aria-pressed={archived} onClick={() => setArchived(true)}>Arquivados</button>
        </div>
        {!archived && !creating && <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}><Icon name="edit" />Novo projeto</button>}
      </div>
      {creating && (
        <section className="card">
          <h2 className="form-title">Novo projeto</h2>
          <ProjectForm initial={EMPTY} submitLabel="Criar projeto" onCancel={() => setCreating(false)}
            onSubmit={async (v) => {
              const r = await api.post<ItemResponse<ProjectRecord>>('/api/projects', payload(v));
              set((list) => [r.item, ...list]);
              setCreating(false);
              toast('Projeto criado.', 'check');
            }} />
        </section>
      )}
      {body}
    </div>
  );
}
